import { IWorkStream } from '../types/WorkStream';
import { toDate } from './timestampUtils';

/**
 * Paid-work funded streams must be claimed via the Paid work
 * reservation flow so the shared £15ph pool stays accurate.
 */
export function isLivingWageFundedWorkStream(
  workStream: Pick<IWorkStream, 'fundingType'>
): boolean {
  return workStream.fundingType === 'overhear_living_wage';
}

/**
 * Get the reference date for age calculation (startDate if available, otherwise createdAt)
 * @param workStream The work stream
 * @returns Date object or null if neither date exists
 */
export function getReferenceDate(workStream: IWorkStream): Date | null {
  // Prefer startDate if available, otherwise use createdAt
  const dateValue = workStream.startDate || workStream.createdAt;
  return toDate(dateValue);
}

/**
 * Calculate the age category of a work stream
 * @param workStream The work stream
 * @returns Age category object with label and color, or null if date cannot be determined
 */
export function getAgeCategory(workStream: IWorkStream): { label: string; color: string } | null {
  const referenceDate = getReferenceDate(workStream);
  
  if (!referenceDate) {
    return null;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  referenceDate.setHours(0, 0, 0, 0);

  const diffTime = today.getTime() - referenceDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const diffMonths = Math.floor(diffDays / 30);

  if (diffDays >= 365) {
    return { label: 'Over 1 year', color: '#d32f2f' }; // red
  } else if (diffDays >= 180) {
    return { label: 'Over 6 months', color: '#f57c00' }; // orange
  } else if (diffDays >= 90) {
    return { label: 'Over 3 months', color: '#fbc02d' }; // yellow/amber
  } else if (diffDays >= 30) {
    return { label: 'Over 1 month', color: '#388e3c' }; // green
  }

  return null; // Less than 1 month old, no badge needed
}

/**
 * Calculate days remaining from endDate to today
 * @param workStream The work stream with optional startDate and endDate
 * @returns Number of days remaining, or null if endDate doesn't exist or is in the past
 */
export function getDaysRemaining(workStream: IWorkStream): number | null {
  const endDate = toDate(workStream.endDate);
  if (!endDate) {
    return null;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);

  const diffTime = endDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays >= 0 ? diffDays : null;
}

/**
 * Calculate total days in the time period (from startDate to endDate)
 * @param workStream The work stream with optional startDate and endDate
 * @returns Total number of days, or null if dates don't exist
 */
export function getTotalDays(workStream: IWorkStream): number | null {
  const startDate = toDate(workStream.startDate);
  const endDate = toDate(workStream.endDate);
  
  if (!startDate || !endDate) {
    return null;
  }

  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);

  const diffTime = endDate.getTime() - startDate.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays >= 0 ? diffDays : null;
}

/**
 * Calculate current day number in the time period (from startDate to today)
 * @param workStream The work stream with optional startDate and endDate
 * @returns Current day number (1-based), or null if startDate doesn't exist
 */
export function getCurrentDay(workStream: IWorkStream): number | null {
  const startDate = toDate(workStream.startDate);
  if (!startDate) {
    return null;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  startDate.setHours(0, 0, 0, 0);

  const diffTime = today.getTime() - startDate.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays >= 0 ? diffDays + 1 : null; // +1 to make it 1-based
}

/**
 * Format countdown display string
 * @param workStream The work stream
 * @returns Formatted string like "Day 37 / 90" or "53 days remaining"
 */
export function formatCountdown(workStream: IWorkStream): string | null {
  const daysRemaining = getDaysRemaining(workStream);
  if (daysRemaining === null) {
    return null;
  }

  const currentDay = getCurrentDay(workStream);
  const totalDays = getTotalDays(workStream);

  if (currentDay !== null && totalDays !== null) {
    return `Day ${currentDay} / ${totalDays}`;
  } else {
    return `${daysRemaining} days remaining`;
  }
}

/**
 * Get color for countdown based on days remaining
 * @param daysRemaining Number of days remaining
 * @returns Color string: green (>30), yellow (7-30), red (<7)
 */
export function getCountdownColor(daysRemaining: number | null): string {
  if (daysRemaining === null) {
    return '#9e9e9e'; // grey
  }
  if (daysRemaining > 30) {
    return '#4caf50'; // green
  } else if (daysRemaining >= 7) {
    return '#ff9800'; // yellow
  } else {
    return '#f44336'; // red
  }
}

